import type { UseFinancingData } from '~/store-v2/financings/useFinancing'
import { nextTick } from 'vue'
import queryClient, { queryKeys } from '~/queryClient'

const REFETCH_DELAY_MS = 1000
const REFETCH_MAX_ATTEMPTS = 3

type FormalizationStep = 'simulation' | 'customer_data' | 'equipment' | 'payment_model' | 'biometry_and_contract' | 'installation' | 'concluded'

const FORMALIZATION_STEPS: FormalizationStep[] = [
  'customer_data',
  'equipment',
  'payment_model',
  'biometry_and_contract',
]

const INSTALLATION_STEPS: FormalizationStep[] = ['installation', 'concluded']

type RouteStep = 'simulation' | 'formalization' | 'installation'

function getStepFromRoute(routePath: string): RouteStep | null {
  if (routePath.startsWith('/simulation/')) return 'simulation'
  if (routePath.startsWith('/customer/') || routePath.startsWith('/company/')) return 'formalization'
  if (routePath.startsWith('/installation/')) return 'installation'
  return null
}

function getRedirectPath(step: FormalizationStep | undefined, projectId: string, personType?: string): string | null {
  if (!step || !projectId) return null
  if (step === 'simulation') return `/simulation/result/${projectId}`
  if (FORMALIZATION_STEPS.includes(step)) {
    return personType === 'PF' ? `/customer/${projectId}` : `/company/${projectId}`
  }
  if (step === 'installation' || step === 'concluded') return `/installation/${projectId}`
  return null
}

function isStepAllowed(step: FormalizationStep | undefined, allowedSteps: readonly FormalizationStep[]): boolean {
  return Boolean(step && allowedSteps.includes(step))
}

async function refetchUntilStepAllowed(
  projectId: string,
  allowedSteps: readonly FormalizationStep[],
): Promise<boolean> {
  const queryKey = queryKeys.FINANCING.DETAILS(projectId)

  for (let attempt = 0; attempt < REFETCH_MAX_ATTEMPTS; attempt++) {
    await queryClient.refetchQueries({ queryKey })
    const data = queryClient.getQueryData<UseFinancingData>(queryKey)
    const step = data?.step as FormalizationStep | undefined

    if (isStepAllowed(step, allowedSteps)) return true
    if (attempt < REFETCH_MAX_ATTEMPTS - 1) {
      await new Promise(resolve => setTimeout(resolve, REFETCH_DELAY_MS))
    }
  }

  return false
}

function redirectIfNeeded(
  router: ReturnType<typeof useRouter>,
  redirectPath: string | null,
  currentPath: string,
) {
  if (redirectPath && redirectPath !== currentPath) {
    nextTick(() => router.replace(redirectPath))
  }
}

export default async function validateFormalizationStep(
  financing: UseFinancingData | undefined,
  projectId: string,
  routePath: string,
  router: ReturnType<typeof useRouter>,
) {
  if (!financing?.step) return

  const currentStep = financing.step as FormalizationStep
  const personType = financing.person_type
  const nextStep = getStepFromRoute(routePath)

  if (!nextStep) return

  if (currentStep === 'simulation') {
    if (nextStep === 'formalization') {
      const allowed = await refetchUntilStepAllowed(projectId, FORMALIZATION_STEPS)
      if (!allowed) {
        redirectIfNeeded(router, getRedirectPath(currentStep, projectId, personType), routePath)
      }
      return
    }
    if (nextStep === 'installation') {
      const allowed = await refetchUntilStepAllowed(projectId, INSTALLATION_STEPS)
      if (!allowed) {
        redirectIfNeeded(router, getRedirectPath(currentStep, projectId, personType), routePath)
      }
      return
    }
    if (nextStep === 'simulation') return
  }

  if (FORMALIZATION_STEPS.includes(currentStep) && nextStep === 'installation') {
    redirectIfNeeded(router, getRedirectPath(currentStep, projectId, personType), routePath)
  }
}
